/**
 * Entry point of the SPA.
 *
 * Three things happen, in this order, and the order is the contract:
 *
 * 1. **The runtime configuration is loaded** (TASK-0076). The API address belongs to the deployment, not to the bundle, so
 *    it is read from `config.json` next to this document before anything else runs. A Production deployment without a
 *    usable file gets the failure screen below instead of an application that quietly calls the wrong host.
 * 2. The locale is resolved and applied to `<html>` **before** React renders. `index.html` already ships
 *    `lang="fa-IR" dir="rtl"` (so the document is correct with JavaScript disabled), and this call reconciles that
 *    default with the visitor's stored or detected preference before the first component runs — which is what prevents a
 *    visible flip from right-to-left to left-to-right on a returning English visitor.
 * 3. React mounts, and only now does `App` start the silent session restore, which is the first API call of the session.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { App } from '@/App';
import { ConfigurationError, configureRuntime } from '@/lib/configureRuntime';
import { resolveInitialLocale, setLocale } from '@/lib/i18n';
import { renderStartupError } from '@/lib/startupError';
import { AppProviders } from '@/providers/AppProviders';
import '@/styles/globals.css';

const container = document.getElementById('root');

if (!container) {
  throw new Error('The root container (#root) is missing from index.html.');
}

/**
 * Starts the application once the deployment's configuration is known.
 *
 * The message the failure screen shows is the problems themselves — never a configured value — so a screenshot of it can
 * be handed to whoever owns the host.
 */
async function bootstrap(): Promise<void> {
  try {
    const config = await configureRuntime({
      onWarning: (problems) => {
        // Development only: the file is optional there, so this is information rather than a failure.
        console.warn(
          '[prompt-forge] runtime configuration is incomplete; the build-time defaults apply:',
          problems,
        );
      },
    });

    console.info('[prompt-forge] API base URL:', config.apiBaseUrl, `(from ${config.source})`);
  } catch (error) {
    if (error instanceof ConfigurationError) {
      renderStartupError(container as HTMLElement, error.problems, error.configUrl);
      return;
    }

    throw error;
  }

  const initialLocale = resolveInitialLocale();

  // Fire-and-forget: the document attributes are applied synchronously inside `setLocale`, and only the resource swap
  // is asynchronous (the bundles are already in memory, so no request is involved).
  void setLocale(initialLocale);

  createRoot(container as HTMLElement).render(
    <StrictMode>
      <AppProviders>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </AppProviders>
    </StrictMode>,
  );
}

void bootstrap();
